import useMediaQuery from 'hooks/useMediaQuery';
import {
  OrderAPIDeliveryMethod,
  useBuildNewOrder,
  useReorder,
} from 'lib/order';
import { getFirstName } from 'lib/utils';
import menu from 'lib/home/data/raw.json';
import { useCallback, useMemo } from 'react';
import getGreeting from 'lib/datetime/greeting';
import config, { routePaths } from 'appConfig';
import { pluralize } from 'utils';
import {
  DELIVERY_TIMESLOT_DURATION_HOURS,
  minutesToHours,
} from 'lib/deliveryDateTimes';
import datetime, { formatDate, formatTime } from 'lib/datetime';
import notification from 'lib/notification';
import { sendEvent } from 'lib/gtm';
import { useRouter } from 'next/router';
import { useHomePage } from '..';
import { COUPON_CODE_QUERY_PARAM } from 'lib/coupons';

type ImageSrc = {
  src: string;
  alt: string;
};

export type TextContent = {
  headline: string;
  title: string;
  description?: string;
  caption?: string;
};

type ButtonAction = {
  href?: string;
  onClick?: () => Promise<void> | void;
  node: React.ReactNode;
  isLoading?: boolean;
  className?: string;
};

const useIntroBanner = () => {
  const {
    isAuthLoading,
    isOrdersLoading,
    totalOrders,
    user,
    isLastCompletedOrderLoading,
    lastCompletedOrder,
  } = useHomePage();

  const router = useRouter();

  const { isReplicating, onReorder } = useReorder();
  const {
    isLoading: isBuildingNewOrder,
    onBuildNewOrder,
    showBuildNewOrderModal,
    setShowBuildNewOrderModal,
    quantity,
  } = useBuildNewOrder();

  const isMobile = useMediaQuery(
    'only screen and (max-width: 768px)',
  );

  const hasOrders = useMemo(() => totalOrders > 0, [totalOrders]);
  const customizableMeal = menu.product_sections[0];

  const handleReorder = useCallback(
    async (orderId?: string) => {
      if (!orderId) return;

      await onReorder({
        identifier: orderId,
        onError: (message) => {
          notification.error(message);
        },
        onWarning: (
          productNames,
          order,
          isSomeProductUnPurchasable,
        ) => {
          notification.warning(
            <div>
              {isSomeProductUnPurchasable
                ? 'Oops! These items were available in your previous order, but isn’t eligible for purchase under your account now. 😓 '
                : 'Oops! These items are out of stock and were not added to your cart: '}{' '}
              <br />
              <ul style={{ listStyle: 'inside' }}>
                {productNames.map((productName) => (
                  <li key={productName}>{productName}</li>
                ))}
              </ul>
              {!isSomeProductUnPurchasable &&
                'You can either checkout with the rest of the items or add more to your cart! 🥳'}
            </div>,
            null,
            { autoClose: 10000 },
          );

          sendEvent('reorder', {
            order_id: order.id,
            content_type: 'button',
            content_id: 'reorder_from_home_banner',
          });
        },
        onSuccess: (order, message) => {
          notification.success(message);

          sendEvent('reorder', {
            order_id: order.id,
            content_type: 'button',
            content_id: 'reorder_from_home_banner',
          });

          router.push(routePaths.CHECKOUT);
        },
      });
    },
    [onReorder, router],
  );

  const handleBuildNewOrder = useCallback(
    async ({ clearCart }: { clearCart: boolean }) => {
      await onBuildNewOrder(clearCart, {
        onSuccess: () => {
          router.push(routePaths.CATEGORIES);
        },
      });
    },
    [onBuildNewOrder, router],
  );

  const formatDeliveryDate = ({
    deliveryDate,
    duration = DELIVERY_TIMESLOT_DURATION_HOURS,
  }: {
    deliveryDate: string | null;
    duration?: number;
  }) => {
    if (!deliveryDate) return 'N/A';

    const timezone = config.timezone;

    const formattedDate = formatDate(deliveryDate, {
      // @ts-ignore
      timezone,
      format: 'DD MMM, ddd',
    });

    const formattedStartTime = formatTime(deliveryDate, { timezone });
    const formattedEndTime = formatTime(
      datetime(deliveryDate).add(duration, 'hours'),
      { timezone },
    );

    return `${formattedDate}, ${formattedStartTime} - ${formattedEndTime}`;
  };

  const image = useMemo((): ImageSrc => {
    if (isAuthLoading) return { src: '', alt: '' };

    if (user) {
      if (isOrdersLoading || isLastCompletedOrderLoading)
        return { src: '', alt: '' };

      if (hasOrders)
        return {
          src: `/assets/images/landing/${
            isMobile
              ? 'yummy-butter-chicken-mobile'
              : 'yummy-butter-chicken'
          }.png`,
          alt: 'Hero Banner',
        };

      return {
        src: `/assets/images/landing/${
          isMobile
            ? 'existing-customer-no-order-mobile'
            : 'existing-customer-no-order'
        }.png`,
        alt: 'Hero Banner',
      };
    }

    return {
      src: `/assets/images/landing/${
        isMobile ? 'new-customer-mobile' : 'new-customer-shadow'
      }.png`,
      alt: 'Hero Banner',
    };
  }, [
    hasOrders,
    isAuthLoading,
    isLastCompletedOrderLoading,
    isMobile,
    isOrdersLoading,
    user,
  ]);

  const textContent = useMemo((): TextContent => {
    if (isAuthLoading) return { headline: '', title: '' };

    if (user) {
      if (isOrdersLoading || isLastCompletedOrderLoading)
        return { headline: '', title: '' };
      const firstName = getFirstName(user ?? {});

      return {
        headline: `${getGreeting()}, ${firstName}!`,
        title: hasOrders
          ? 'Ready to Re-fuel?'
          : isMobile
          ? 'Ready to Order?'
          : 'Ready for Your First Yummy Order?',
        description: hasOrders
          ? lastCompletedOrder
            ? `Your last order with ${
                lastCompletedOrder.total_items ?? 0
              } ${pluralize(
                lastCompletedOrder.total_items ?? 0,
                'item',
              )} was delivered on ${formatDeliveryDate({
                deliveryDate: lastCompletedOrder.delivery_datetime,
                duration:
                  lastCompletedOrder.delivery_method ===
                  OrderAPIDeliveryMethod.CLICK_AND_COLLECT
                    ? minutesToHours(
                        lastCompletedOrder.timeframe ?? 0,
                      )
                    : undefined,
              })}.`
            : ''
          : `Start small, stay consistent. Your first Yummy Bros meal is an easy way to kickstart a healthier, stronger you. <br /><div class="tw-mt-4">Apply <strong>${config.welcomeCouponCode}</strong> for <strong>$10 OFF + FREE delivery</strong> with min. $50 spend.</div>`,
      };
    }

    return {
      headline: 'Healthy & Bold-Tastin’',
      title: isMobile
        ? 'Meal Preps.<br />Built Your Way.'
        : 'Meal Preps.<br />Built Your Way.<br />Delivered Islandwide.',
      caption: isMobile ? 'Delivered Islandwide.' : undefined,
      description: isMobile
        ? undefined
        : 'Pick your protein, carbs, and veggies — cooked fresh, portioned right, and ready to eat.',
    };
  }, [
    hasOrders,
    isAuthLoading,
    isLastCompletedOrderLoading,
    isMobile,
    isOrdersLoading,
    lastCompletedOrder,
    user,
  ]);

  const primaryButton = useMemo((): ButtonAction | null => {
    if (isAuthLoading) return null;

    if (user) {
      if (isOrdersLoading || isLastCompletedOrderLoading) return null;

      if (hasOrders) {
        if (!lastCompletedOrder) return null;

        return {
          onClick: async () =>
            await handleReorder(lastCompletedOrder.id),
          node: 'Re-Order Now',
          isLoading: isReplicating,
        };
      }

      return {
        href: `${routePaths.CATEGORIES}?${COUPON_CODE_QUERY_PARAM}=${config.welcomeCouponCode}`,
        node: 'Claim $10 Off + Free Delivery',
      };
    }

    return isMobile
      ? null
      : {
          href: `${routePaths.PRODUCTS}?category_ids=${customizableMeal.query?.category_ids?.[0]}`,
          node: 'Customise Your Meal',
        };
  }, [
    customizableMeal.query?.category_ids,
    handleReorder,
    hasOrders,
    isAuthLoading,
    isLastCompletedOrderLoading,
    isMobile,
    isOrdersLoading,
    isReplicating,
    lastCompletedOrder,
    user,
  ]);

  const secondaryButton = useMemo((): ButtonAction | null => {
    if (isAuthLoading) return null;

    if (user) {
      if (
        isOrdersLoading ||
        isLastCompletedOrderLoading ||
        !hasOrders
      )
        return null;

      return {
        onClick: async () => {
          if (quantity > 0) {
            setShowBuildNewOrderModal(true);
          } else {
            await handleBuildNewOrder({ clearCart: false });
          }
        },
        node: (
          <span className="tw-leading-[1.1]">
            View Menu /<br /> Build New Order
          </span>
        ),
        isLoading: isBuildingNewOrder,
        className:
          'tw-text-[#1d1d1d] tw-bg-secondary tw-border-secondary hover:tw-bg-secondary-600',
      };
    }

    return {
      href: routePaths.CATEGORIES,
      node: 'Explore Menu',
      className:
        'tw-flex [&>button]:tw-text-[#1d1d1d] [&>button]:tw-bg-secondary [&>button]:tw-border-secondary [&>button]:hover:tw-bg-secondary-600',
    };
  }, [
    handleBuildNewOrder,
    hasOrders,
    isAuthLoading,
    isBuildingNewOrder,
    isLastCompletedOrderLoading,
    isOrdersLoading,
    quantity,
    setShowBuildNewOrderModal,
    user,
  ]);

  const additionalButton = useMemo((): ButtonAction | null => {
    if (isAuthLoading) return null;

    if (user) {
      if (
        isOrdersLoading ||
        isLastCompletedOrderLoading ||
        !hasOrders
      )
        return null;

      return isMobile
        ? null
        : {
            href: routePaths.ORDERS,
            node: 'Repeat Another Order',
            className:
              'tw-flex [&>button]:tw-text-[#1d1d1d] [&>button]:tw-bg-secondary [&>button]:tw-border-secondary [&>button]:hover:tw-bg-secondary-600',
          };
    }

    return null;
  }, [
    hasOrders,
    isAuthLoading,
    isLastCompletedOrderLoading,
    isMobile,
    isOrdersLoading,
    user,
  ]);

  const loadingState =
    isAuthLoading || user
      ? isOrdersLoading || isLastCompletedOrderLoading
      : false;

  return {
    loading: loadingState,
    image,
    textContent,
    primaryButton,
    secondaryButton,
    additionalButton,
    user,
    handleBuildNewOrder,
    showBuildNewOrderModal,
    setShowBuildNewOrderModal,
  };
};

export default useIntroBanner;
